import { useCallback, useMemo, useRef, useState } from 'react';
import Slider, { type Settings as SlickSettings } from 'react-slick';
import TrackWrapper from '@/common/TrackWrapper';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  ButtonsInnerWrapper,
  ButtonsOuterWrapper,
  CarouselButton,
  INACTIVE_SLIDE_OPACITY,
  SlideWrapper
} from './MarketingCarousel.styles';
import Slide from './Slide';
import useSlideCenterPadding from './useSlideCenterPadding';
import useSlideContents from './useSlideContents';

function useSlideContentsAndLongestHeadings() {
  const slideContents = useSlideContents();

  // Find the longest heading & subheading for spacing purposes, so all slides
  // are the same size regardless of their individual subheading length
  const { longestHeading, longestSubHeading } = useMemo(
    () =>
      slideContents.reduce(
        (acc, current) => {
          if (current.heading.length > acc.longestHeading.length) {
            acc.longestHeading = current.heading;
          }

          if (current.subHeading.length > acc.longestSubHeading.length) {
            acc.longestSubHeading = current.subHeading;
          }

          return acc;
        },
        { longestHeading: '', longestSubHeading: '' }
      ),
    [slideContents]
  );

  return { slideContents, longestHeading, longestSubHeading };
}

export default function MarketingCarousel() {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const sliderRef = useRef<Slider>(null);

  const isMobile = useIsMobile();
  const { slideContents, longestHeading, longestSubHeading } =
    useSlideContentsAndLongestHeadings();
  const slideCenterPadding = useSlideCenterPadding();
  const trackEvent = useTrackEvent();

  const handleBeforeChange = useCallback((_current: number, next: number) => {
    setActiveSlideIndex(next);
  }, []);

  const handleAfterChange = useCallback(
    (index: number) => {
      trackEvent('Marketing Carousel', {
        action: 'swiped',
        description: 'Slide changed',
        text: slideContents[index]?.heading || '',
        index
      });
    },
    [slideContents, trackEvent]
  );

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  const sliderSettings = useMemo<SlickSettings>(
    () => ({
      afterChange: handleAfterChange,
      arrows: false,
      beforeChange: handleBeforeChange,
      centerMode: true,
      centerPadding: `${slideCenterPadding}px`,
      cssEase: isMobile ? undefined : 'cubic-bezier(0.33, 1.00, 0.68, 1.00)',
      dots: false,
      draggable: true,
      infinite: true,
      slidesToScroll: 1,
      slidesToShow: 1,
      speed: isMobile ? undefined : 800,
      swipeToSlide: true
    }),
    [handleAfterChange, handleBeforeChange, isMobile, slideCenterPadding]
  );

  return (
    <TrackWrapper
      event={{
        customAttributes: { action: 'viewed', section: 'Marketing Carousel' },
        eventName: 'Home Feed Section'
      }}
      trackOnce
      className="py-7xl sm:pt-8xl md:pb-8xl"
    >
      <div className="relative">
        <Slider ref={sliderRef} {...sliderSettings}>
          {slideContents.map((slide, index) => (
            <SlideWrapper key={index} data-testid="marketing-carousel-slide">
              <Slide
                {...slide}
                opacity={
                  index === activeSlideIndex ? 1 : INACTIVE_SLIDE_OPACITY
                }
                spacerHeading={longestHeading}
                spacerSubHeading={longestSubHeading}
              />
            </SlideWrapper>
          ))}
        </Slider>

        <span className="sr-only" role="status" aria-live="polite">
          {`Slide ${activeSlideIndex + 1} of ${slideContents.length}`}
        </span>

        <ButtonsOuterWrapper>
          <ButtonsInnerWrapper>
            <CarouselButton
              direction="left"
              onClick={goToPrev}
              aria-label="Previous slide"
              data-analytics-id="Marketing Carousel"
              data-analytics-attributes={JSON.stringify({
                description: 'prev'
              })}
              data-testid="marketing-carousel-prev"
            />
            <CarouselButton
              direction="right"
              onClick={goToNext}
              aria-label="Next slide"
              data-analytics-id="Marketing Carousel"
              data-analytics-attributes={JSON.stringify({
                description: 'next'
              })}
              data-testid="marketing-carousel-next"
            />
          </ButtonsInnerWrapper>
        </ButtonsOuterWrapper>
      </div>
    </TrackWrapper>
  );
}
